import { ChangeEvent } from 'react';
import {
  Alert,
  Box,
  Card,
  Checkbox,
  Icon,
  Text,
  Tooltip,
} from '@nimbus-ds/components';
import { InfoCircleIcon } from '@nimbus-ds/icons';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import { MetaplanFormProps } from '../MetaplanForm';

interface PromotionCardProps
  extends Pick<MetaplanFormProps, 'values' | 'setFieldValue' | 'errors'> {
  disabled?: boolean;
}

export function PromotionCard({
  values: { promotionCombinations },
  setFieldValue,
  disabled = false,
}: Readonly<PromotionCardProps>) {
  const t = useTranslationCatalog();

  const handleCheckboxChange =
    (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.checked;
      const updatedCombinations = {
        ...promotionCombinations,
        [field]: newValue,
      };
      setFieldValue('promotionCombinations', updatedCombinations);
    };

  const getSelectedCount = (): number =>
    Object.values(promotionCombinations).filter(Boolean).length;

  const getAlertAppearance = (): 'primary' | 'warning' => {
    const count = getSelectedCount();
    return count === 2 ? 'primary' : 'warning';
  };

  const selectedCount = getSelectedCount();

  return (
    <Card>
      <Card.Header title={t('subscriptions.form.promotions.title')} />
      <Card.Body>
        <Box display="flex" flexDirection="column" gap="3">
          <Box display="flex" flexDirection="column" gap="2">
            <Text fontSize="caption">
              {t('subscriptions.form.promotions.duringSubscription')}
            </Text>
            <Box display="flex" flexDirection="column" gap="2">
              <Box display="flex" alignItems="center" gap="1">
                <Checkbox
                  name="combinesWithQuantityDiscounts"
                  label={t(
                    'subscriptions.form.promotions.combinesWithQuantityDiscounts',
                  )}
                  checked={promotionCombinations.combinesWithQuantityDiscounts}
                  onChange={handleCheckboxChange(
                    'combinesWithQuantityDiscounts',
                  )}
                  disabled={disabled}
                />
                <Tooltip
                  content={t(
                    'subscriptions.form.promotions.combinesWithQuantityDiscountsTooltip',
                  )}
                  position="top"
                >
                  <Icon
                    source={<InfoCircleIcon size="small" />}
                    color="primary-interactive"
                  />
                </Tooltip>
              </Box>
              <Box display="flex" alignItems="center" gap="1">
                <Checkbox
                  name="combinesWithPriceDiscounts"
                  label={t(
                    'subscriptions.form.promotions.combinesWithPriceDiscounts',
                  )}
                  checked={promotionCombinations.combinesWithPriceDiscounts}
                  onChange={handleCheckboxChange('combinesWithPriceDiscounts')}
                  disabled={disabled}
                />
                <Tooltip
                  content={t(
                    'subscriptions.form.promotions.combinesWithPriceDiscountsTooltip',
                  )}
                  position="top"
                >
                  <Icon
                    source={<InfoCircleIcon size="small" />}
                    color="primary-interactive"
                  />
                </Tooltip>
              </Box>
              <Checkbox
                name="combinesWithFreeShipping"
                label={t(
                  'subscriptions.form.promotions.combinesWithFreeShipping',
                )}
                checked={promotionCombinations.combinesWithFreeShipping}
                onChange={handleCheckboxChange('combinesWithFreeShipping')}
                disabled={disabled}
              />
            </Box>
          </Box>

          <Box display="flex" flexDirection="column" gap="2">
            <Text fontSize="caption">
              {t('subscriptions.form.promotions.firstChargeOnly')}
            </Text>
            <Box display="flex" flexDirection="column" gap="1">
              <Checkbox
                name="combinesWithCartAmountDiscounts"
                label={t(
                  'subscriptions.form.promotions.combinesWithCartAmountDiscounts',
                )}
                checked={promotionCombinations.combinesWithCartAmountDiscounts}
                onChange={handleCheckboxChange(
                  'combinesWithCartAmountDiscounts',
                )}
                disabled={disabled}
              />
              <Checkbox
                name="combinesWithAppDiscounts"
                label={t(
                  'subscriptions.form.promotions.combinesWithAppDiscounts',
                )}
                checked={promotionCombinations.combinesWithAppDiscounts}
                onChange={handleCheckboxChange('combinesWithAppDiscounts')}
                disabled={disabled}
              />
            </Box>
          </Box>

          {selectedCount >= 2 && (
            <Box display="flex" flexDirection="column" gap="1">
              <Text color="neutral-textHigh">
                {t('subscriptions.form.promotions.alertTitlePrefix')}{' '}
                <Text as="span" fontWeight="bold">
                  {t('subscriptions.form.promotions.alertTitleCount', {
                    count: selectedCount,
                  })}
                </Text>{' '}
                {t('subscriptions.form.promotions.alertTitleSuffix')}
              </Text>
              <Alert appearance={getAlertAppearance()}>
                {t('subscriptions.form.promotions.alertContent')}
              </Alert>
            </Box>
          )}
        </Box>
      </Card.Body>
    </Card>
  );
}
